import { getSupabaseClient } from '../../../../core/supabase/client.js';
import type { SessionDto } from '../dtos/SessionDto.js';

/** Shape returned by the export_sessions_by_project RPC (joint sessions + location + project_location + achiever). */
export type SessionExportDto = SessionDto & {
  achiever_profile?: { email?: string | null; full_name?: string | null; phone?: string | null } | null;
  location?: {
    id: string;
    name: string | null;
    code: string | null;
    address_line: string | null;
    full_address_cache: string | null;
    categories: string[] | null;
    province_code: string | null;
    province_name: string | null;
    ward_code: string | null;
    ward_name: string | null;
  } | null;
  project_location?: {
    geo_fence_radius_m: number | null;
    custom_category: string | null;
    custom_code: string | null;
    metadata: Record<string, unknown> | null;
  } | null;
};

const FIELDS = `
  id, org_id, project_id, created_by, achiever_id,
  started_at, ended_at, scheduled_start_at, scheduled_end_at,
  location_id, started_lat, started_lng, ended_lat, ended_lng,
  minimum_geo_fence_meters, minimum_duration_minutes, required_photo_count,
  start_photo_urls, end_photo_urls, notes, created_at, updated_at,
  achiever_profile:profiles!achiever_id(email),
  location:locations!location_id(name, province_code)
`;

/** Row shape from the sessions_achiever_overlaps RPC (minimal — used only to warn about conflicts). */
export interface SessionOverlapDto {
  id: string;
  project_id: string;
  location_id: string | null;
  scheduled_start_at: string;
  scheduled_end_at: string;
}

export class SessionRemoteDataSource {
  private get supabase() { return getSupabaseClient() as any; }

  async list(options: { orgId: string; projectId?: string; locationId?: string; provinceCodes?: string[]; achieverIds?: string[]; dateFrom?: string; dateTo?: string; status?: string; unassignedOnly?: boolean; limit?: number; offset?: number }): Promise<{ data: SessionDto[]; total: number }> {
    if (options.achieverIds?.length === 0) return { data: [], total: 0 };

    const fields = options.provinceCodes?.length
      ? FIELDS.replace('location:locations!location_id(name, province_code)', 'location:locations!location_id!inner(name, province_code)')
      : FIELDS;
    let query = this.supabase.from('sessions').select(fields, { count: 'exact' })
      .eq('org_id', options.orgId)
      .order('scheduled_start_at', { ascending: false });
    if (options.projectId) query = query.eq('project_id', options.projectId);
    if (options.locationId) query = query.eq('location_id', options.locationId);
    if (options.provinceCodes?.length) query = query.in('location.province_code', options.provinceCodes);
    if (options.achieverIds?.length) query = query.in('achiever_id', options.achieverIds);
    if (options.dateFrom) query = query.gte('scheduled_start_at', options.dateFrom);
    if (options.dateTo) query = query.lte('scheduled_start_at', options.dateTo);
    if (options.unassignedOnly) query = query.is('achiever_id', null);
    // deriveSessionStatus: completed=ended_at≠null, active=started_at≠null∧ended_at=null, scheduled=started_at=null
    if (options.status === 'completed') query = query.not('ended_at', 'is', null);
    else if (options.status === 'active') query = query.not('started_at', 'is', null).is('ended_at', null);
    else if (options.status === 'scheduled') query = query.is('started_at', null);
    if (typeof options.limit === 'number' && typeof options.offset === 'number') {
      query = query.range(options.offset, options.offset + options.limit - 1);
    } else if (typeof options.limit === 'number') {
      query = query.limit(options.limit);
    }
    const { data, error, count } = await query;
    if (error) throw new Error(error.message);
    return { data: data ?? [], total: count ?? 0 };
  }

  async getById(id: string): Promise<SessionDto | null> {
    const { data, error } = await this.supabase.from('sessions').select(FIELDS).eq('id', id).maybeSingle();
    if (error) throw new Error(error.message);
    return data ?? null;
  }

  async create(payload: Partial<SessionDto> & { org_id: string; project_id: string }): Promise<SessionDto | null> {
    const { data: { user } } = await this.supabase.auth.getUser();
    const { data, error } = await this.supabase.from('sessions')
      .insert({ ...payload, created_by: user?.id })
      .select(FIELDS).maybeSingle();
    if (error) throw new Error(error.message);
    return data ?? null;
  }

  async update(id: string, payload: Partial<SessionDto>): Promise<SessionDto | null> {
    const { data, error } = await this.supabase.from('sessions').update(payload).eq('id', id).select(FIELDS).maybeSingle();
    if (error) throw new Error(error.message);
    return data ?? null;
  }

  async delete(id: string): Promise<void> {
    const { error } = await this.supabase.from('sessions').delete().eq('id', id);
    if (error) throw new Error(error.message);
  }

  async checkAchieverOverlap(options: {
    achieverId: string;
    scheduledStartAt: string;
    scheduledEndAt: string;
    excludeSessionId?: string;
  }): Promise<SessionOverlapDto[]> {
    const { data, error } = await this.supabase.rpc('sessions_achiever_overlaps', {
      p_achiever_id: options.achieverId,
      p_start: options.scheduledStartAt,
      p_end: options.scheduledEndAt,
      p_exclude_session_id: options.excludeSessionId ?? null,
    });
    if (error) throw new Error(error.message);
    return (data ?? []) as SessionOverlapDto[];
  }

  async exportByProject(options: {
    orgId: string;
    projectId: string;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<SessionExportDto[]> {
    const { data, error } = await this.supabase.rpc('export_sessions_by_project', {
      p_org_id: options.orgId,
      p_project_id: options.projectId,
      p_date_from: options.dateFrom ?? null,
      p_date_to: options.dateTo ?? null,
    });
    if (error) throw new Error(error.message);
    return (data ?? []) as SessionExportDto[];
  }

  async search(options: {
    orgId: string;
    projectId?: string;
    searchQuery: string;
    limit?: number;
    offset?: number;
  }): Promise<{ data: SessionDto[]; total: number }> {
    const { data, error } = await this.supabase.rpc('search_sessions', {
      p_org_id: options.orgId,
      p_project_id: options.projectId ?? null,
      search_query: options.searchQuery ?? '',
      row_limit: options.limit ?? 20,
      row_offset: options.offset ?? 0,
    });
    if (error) throw new Error(error.message);
    const rows = (data ?? []) as (SessionDto & { location?: any })[];
    return { data: rows, total: rows.length };
  }
}
